import { useEffect, useState, useContext, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    useTheme,
    TextField,
    Chip,
    Skeleton,
    MenuItem,
    Tooltip,
    FormControl,
    Select,
} from '@mui/material';
import {
    useGetCompetitionEvolutionQuery,
    useGetAvailableDomainsQuery,
    useGetCompetitionEvolutionKpisQuery,
    useGetVisibilityKpisQuery,
    useGetCompetitionEvolutionVisibilityCardQuery,
} from 'src/context/api/competitorsMonitoring/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { FixedSizeList as List } from 'react-window';
import {
    setFilterKeyword,
} from 'src/context/reducers/filter-settings/filterSlice';
import { t } from 'i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import countriesJson from 'src/utils/countries.json';
import { CardBar } from 'src/components/visibility/components/CardBar';
import { fDate } from 'src/utils/format-time';
import { mapLevelToLabel } from 'src/components/visibility/utils';
import type { TopCompetitorStat } from 'src/components/visibility/components/TopCompetitorsCard';
import { VisibilityContext } from '../../../../../components/visibility/components/VisibilityProvider';
import Iconify from '../../../../../components/iconify';
import { CountryFilter } from '../../../../../components/visibility/components/CountryFilter';
import { useCountryData } from '../../../../../components/visibility/hooks/useCountryData';
import { SearchTermAnalysisHeader } from '../../../../../components/visibility/components/SearchTermAnalysisHeader';
import ChartEvolution from '../../../../../components/visibility/components/ChartEvolution';
import { useGetBrandColorsQuery } from 'src/context/api/brandColor/api';
import InfiniteScrollKeywords from "src/sections/overview/dashboards/views/InfiniteScrollKeywords";
import { dashboardPaths } from 'src/routes/dashboardPaths';

const formatRate = (raw: number | null | undefined) => {
    const value = raw ?? 0;
    if (value > 0 && value < 1) return '<1%';
    return `${Math.round(value)}%`;
};

export default function CompetitionEvolution() {
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);
    const [isAbsMode, setIsAbsMode] = useState<boolean>(false);
    const [selected, setSelected] = useState<'paid' | 'blended' | 'winrate'>('paid');
    const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
    const [domainFilter, setDomainFilter] = useState('');
    const [hasUserSelected, setHasUserSelected] = useState(false);
    const [hoveredClientDomain, setHoveredClientDomain] = useState<string | null>(null);
    const [graphKeywords, setGraphKeywords] = useState<string[]>([]);
    const dispatch = useDispatch();
    const theme: any = useTheme();
    const { countriesData, isLoading } = useCountryData();
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const ciContext = useContext(VisibilityContext);
    const location = useLocation();
    const navigate = useNavigate();
    const ITEM_HEIGHT = 56;

    const handleKeywordFilterChange = (value: any) => {
        if (filterStore?.keywords) {
            if (filterStore?.keywords.includes(value)) {
                dispatch(setFilterKeyword(filterStore?.keywords.filter((keyword: any) => keyword !== value)))
            } else {
                const updatedKeywords = [...filterStore.keywords, value];
                dispatch(setFilterKeyword(updatedKeywords))
            }
        }
    };


    // Essayer d'utiliser le contexte centralisé si disponible
    const context = useContext(VisibilityContext);


    // --- KPI search-term analysis data for CardBar (same metrics as SearchTermsAnalysis) ---
    const EMPTY_KEYWORDS: string[] = useMemo(() => [], []);
    const isShoppingEnabled = workspace?.template === 'E-commerce' || workspace?.shoppingIncluded === true;
    const normalizedAdType = (Array.isArray(filterStore?.adType) && filterStore.adType.length === 0)
        ? (isShoppingEnabled ? ['TXT', 'PLA'] : ['TXT'])
        : filterStore?.adType;

    const skipStaQuery =
        !filterStore.start ||
        !filterStore.end ||
        !workspace?.id ||
        (!selectedCountry && (!Array.isArray(filterStore.countries) || filterStore.countries.length === 0));

    const {
        data: competitionEvolutionKpis,
        isFetching: isFetchingCompetitionEvolutionKpis,
    } = useGetCompetitionEvolutionKpisQuery(
        {
            startDate: filterStore.start !== undefined ? fDate(filterStore.start, 'yyyy-MM-dd') : null,
            endDate: filterStore.end !== undefined ? fDate(filterStore.end, 'yyyy-MM-dd') : null,
            compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, 'yyyy-MM-dd') : null,
            compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, 'yyyy-MM-dd') : null,
            timestep: filterStore.timestep,
            devices: filterStore.devices,
            countries: selectedCountry ? [selectedCountry] : filterStore.countries,
            keywords: EMPTY_KEYWORDS,
            adType: normalizedAdType,
            workspaceId: workspace?.id,
        },
        {
            skip: skipStaQuery,
        }
    );

    const {
        data: overviewKpisData,
        isFetching: isFetchingOverviewKpis,
    } = useGetVisibilityKpisQuery(
        {
            startDate: filterStore.start !== undefined ? fDate(filterStore.start, 'yyyy-MM-dd') : null,
            endDate: filterStore.end !== undefined ? fDate(filterStore.end, 'yyyy-MM-dd') : null,
            compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, 'yyyy-MM-dd') : null,
            compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, 'yyyy-MM-dd') : null,
            countries: selectedCountry ? [selectedCountry] : filterStore.countries,
            devices: filterStore.devices,
            keywords: graphKeywords,
            platforms: filterStore.platforms,
            timestep: filterStore.timestep,
            workspaceId: workspace?.id,
        },
        {
            skip: skipStaQuery,
        }
    );

    // Quand l'utilisateur a tout désélectionné, on envoie uniquement le domaine client
    // pour que le backend filtre sur lui seul (sinon il retournerait tous les domaines).
    const apiSelectedDomains =
        hasUserSelected && selectedDomains.length === 0 && workspace?.url
            ? [workspace.url]
            : selectedDomains;

    const { data, isFetching } = useGetCompetitionEvolutionQuery(
        {
            workspaceId: workspace?.id,
            platforms: filterStore?.platforms,
            devices: filterStore?.devices,
            startDate: filterStore?.start,
            keywords: graphKeywords,
            endDate: filterStore?.end,
            isAbsMode,
            adType: filterStore?.adType,
            rateMode: selected,
            timestep: filterStore?.timestep,
            selectedCountry,
            selectedDomains: apiSelectedDomains,
        },
        { skip: !selectedCountry }
    );

    const { data: visibilityCardData, isFetching: isFetchingVisibilityCard } = useGetCompetitionEvolutionVisibilityCardQuery(
        {
            startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
            endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
            devices: filterStore.devices || [],
            keywords: graphKeywords,
            platforms: filterStore?.platforms || [],
            workspaceId: workspace?.id,
            selectedCountry,
        },
        { skip: !selectedCountry || !workspace?.id }
    );

    const buttons = [
        {
            key: 'paid',
            label: t('dashboard.global.paid_presence_rate'),
            icon: 'f7:money-dollar-circle',
            tooltip: isAbsMode
                ? t('dashboard.global.definition_paid_presence_rate_abs_top')
                : t('dashboard.global.definition_paid_presence_rate_top'),
        },
        {
            key: 'blended',
            label: t('dashboard.global.blended_presence_rate'),
            icon: 'proicons:globe',
            tooltip: isAbsMode
                ? t('dashboard.global.definition_blended_presence_rate_abs_top')
                : t('dashboard.global.definition_blended_presence_rate_top'),
        },
    ];

    useEffect(() => {
        if (ciContext?.selectedCountry != null) {
            setSelectedCountry(ciContext.selectedCountry);
        }
    }, [ciContext?.selectedCountry]);

    useEffect(() => {
        const params = new URLSearchParams(location.search || (typeof window !== 'undefined' ? window.location.search : ''));
        const codeParam = params.get('country');
        if (!codeParam) return;
        const mappedName = countriesJson?.find((c: any) => String(c.code).toUpperCase() === String(codeParam).toUpperCase())?.name;
        if (mappedName && countriesData.some((c) => c.country_name === mappedName)) {
            setSelectedCountry(mappedName);
            try {
                ciContext?.setSelectedCountry?.(mappedName);
                if (workspace?.id) localStorage.setItem(`ci.selectedCountry.${workspace.id}`, mappedName);
            } catch { }
        }
        const metricParam = params.get('metric');
        if (metricParam === 'paid' || metricParam === 'blended' || metricParam === 'winrate') {
            setSelected(metricParam);
        }
        const domainParam = params.get('domain');
        if (domainParam && domainParam.trim()) {
            setSelectedDomains([domainParam.trim()]);
            setHasUserSelected(true);
        }
    }, [location.search, countriesData, ciContext, workspace?.id]);

    useEffect(() => {
        if (selectedCountry || !countriesData.length || !workspace?.id) return;
        try {
            const key = `ci.selectedCountry.${workspace.id}`;
            const stored = localStorage.getItem(key);
            if (stored && countriesData.some((c) => c.country_name === stored)) {
                setSelectedCountry(stored);
                ciContext?.setSelectedCountry?.(stored);
            }
        } catch { }
    }, [selectedCountry, countriesData, workspace?.id, ciContext]);

    useEffect(() => {
        if (!selectedCountry && countriesData.length > 0) {
            setSelectedCountry(countriesData[0].country_name);
        }
    }, [selectedCountry, countriesData]);

    const { data: availableDomainsData, isFetching: isLoadingAvailableDomains } = useGetAvailableDomainsQuery(
        {
            workspaceId: workspace?.id,
            platforms: filterStore?.platforms,
            devices: filterStore?.devices,
            startDate: filterStore?.start,
            keywords: graphKeywords,
            endDate: filterStore?.end,
            adType: isShoppingEnabled ? filterStore?.adType : [],
            timestep: filterStore?.timestep,
            selectedCountry: selectedCountry,
        },
        {
            skip: !selectedCountry || !workspace?.id
        }
    );

    const allDomains = availableDomainsData?.data ?? [];
    const graphData = data?.data?.presence_rates_average_metrics_by_date ?? [];

    // Prefetch brand colors dès que availableDomainsData arrive (avant graphData)
    const prefetchDomainNames = useMemo(
        () => Array.from(new Set(allDomains.map((d: any) => d.domain_name).filter(Boolean))),
        [allDomains]
    );
    const { data: prefetchedColors } = useGetBrandColorsQuery(prefetchDomainNames, { skip: prefetchDomainNames.length === 0 });

    const allDomainNames = useMemo(
        () => Array.from(new Set(graphData.map((d: any) => d.domain_name).filter(Boolean))),
        [graphData]
    );

    const { data: brandColors, isSuccess: brandColorsReady } = useGetBrandColorsQuery(allDomainNames, {
        skip: allDomainNames.length === 0,
    });

    const getDomainColor = useMemo(() => {
        return (domainName: string) => {
            return brandColors?.[domainName] ?? '#ddd';
        };
    }, [brandColors]);

    // --- CardBar metrics: pressure pools, top competitors, blended presence weekly series & top metric ---

    // Build "rows" from KPI data (similar to SearchTermsAnalysis, but we only need a subset for aggregates)
    const rows = useMemo(() => (competitionEvolutionKpis as any[] | undefined)?.map((item: any, index: number) => {
        const isNotScrapped = item?.status === 'not scrapped';
        return {
            id: `${item?.country_name ?? 'NA'}__${item?.search_term ?? index}`,
            searchTerm: item?.search_term ?? '',
            country: item?.country_name ?? '',
            trafficShare: item?.traffic_share ?? 0,
            levelRaw: isNotScrapped ? null : (isAbsMode ? item?.abs_level : item?.level),
            blendedPresenceRate: isNotScrapped
                ? null
                : (isAbsMode ? (item?.abs_top_blended_presence_rate ?? 0) : (item?.blended_presence_rate ?? 0)),
            top_5_competitors: Array.isArray(item?.top_5_competitors) ? item.top_5_competitors : [],
        };
    }) ?? [], [competitionEvolutionKpis, isAbsMode]);

    // Solo bidder rate card: main KPI from total_weekly_solo_bidder_rate (visibility card API)
    const soloBidderValue = useMemo(() => {
        const v = visibilityCardData?.data?.total_weekly_solo_bidder_rate;
        return typeof v === 'number' && !Number.isNaN(v) ? v : undefined;
    }, [visibilityCardData?.data?.total_weekly_solo_bidder_rate]);

    const soloBidderWeeklySeries = useMemo(() => {
        const series = visibilityCardData?.data?.visibility_rate_weekly_series;
        if (!Array.isArray(series)) return undefined;
        return series.map((d: { week_start?: string; solo_bidder?: number }) => ({
            week_start: d?.week_start ?? '',
            solo_bidder: typeof d?.solo_bidder === 'number' ? d.solo_bidder : 0,
        }));
    }, [visibilityCardData?.data?.visibility_rate_weekly_series]);

    // Top competitors card data (from overview KPIs: top_3_* if present, else derived from top_5_competitors)
    const topCompetitorsData = useMemo<TopCompetitorStat[]>(() => {
        const firstItem = (overviewKpisData as any[] | undefined)?.[0];
        const candidates =
            firstItem?.top_3_competitors
            ?? firstItem?.top_3_compet
            ?? firstItem?.card_top_3_competitors
            ?? [];

        if (Array.isArray(candidates) && candidates.length > 0) {
            return candidates
                .map((competitor: any) => ({
                    domain: competitor?.domain_name ?? competitor?.domain ?? '',
                    paidPresenceRate: Number(competitor?.paid_presence_rate ?? competitor?.paidPresenceRate ?? 0),
                }))
                .filter((item: TopCompetitorStat) => item.domain.length > 0);
        }

        // Fallback: derive top competitors from top_5_competitors of all overview rows (same as Overview page)
        const stats = new Map<string, { total: number; count: number }>();
        const workspaceDomains = new Set<string>();
        if (workspace?.url) {
            workspaceDomains.add(workspace.url.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, ''));
        }
        if (workspace?.friendlyDomains && Array.isArray(workspace.friendlyDomains)) {
            workspace.friendlyDomains.forEach((d: string) => {
                workspaceDomains.add(d.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, ''));
            });
        }
        (overviewKpisData as any[] | undefined)?.forEach((item: any) => {
            if (!Array.isArray(item?.top_5_competitors)) return;
            item.top_5_competitors.forEach((competitor: any) => {
                const domain = competitor?.domain_name;
                const rate = competitor?.paid_presence_rate;
                if (!domain || typeof rate !== 'number' || rate <= 0) return;
                const normalized = domain.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
                if (workspaceDomains.has(normalized)) return;
                const current = stats.get(domain) ?? { total: 0, count: 0 };
                current.total += rate;
                current.count += 1;
                stats.set(domain, current);
            });
        });
        return Array.from(stats.entries())
            .map(([domain, { total, count }]) => ({
                domain: domain.trim(),
                paidPresenceRate: count ? Math.round((total / count) * 10) / 10 : 0,
            }))
            .filter((item: TopCompetitorStat) => item.domain.length > 0)
            .sort((a, b) => b.paidPresenceRate - a.paidPresenceRate);
    }, [overviewKpisData, workspace?.url, workspace?.friendlyDomains]);

    // Weekly visibility card series (from cest_card_visibility_rate.sql)
    const blendedPresenceRateWeeklySeries = useMemo(() => {
        const series = visibilityCardData?.data?.visibility_rate_weekly_series;
        if (Array.isArray(series)) {
            return series;
        }
        return undefined;
    }, [visibilityCardData?.data?.visibility_rate_weekly_series]);

    // Top metric for visibility card (from cest_card_visibility_rate.sql)
    const blendedPresenceTopMetric = useMemo(() => {
        const value = Number(visibilityCardData?.data?.visibility_rate_total ?? 0);
        if (value > 0) return `${Math.round(value)}%`;
        return undefined;
    }, [visibilityCardData?.data?.visibility_rate_total]);

    const aggregated = useMemo(() => Object.values(
        graphData.reduce((acc: any, row: any) => {
            if (!acc[row.domain_name]) {
                acc[row.domain_name] = { domain_name: row.domain_name, blended: 0, paid: 0, winrate: 0 };
            }
            acc[row.domain_name].blended += (isAbsMode ? row.abs_top_blended_presence_rate : row.top_blended_presence_rate) || 0;
            acc[row.domain_name].paid += (isAbsMode ? row.abs_top_paid_presence_rate : row.top_paid_presence_rate) || 0;
            acc[row.domain_name].winrate += (isAbsMode ? row.abs_top_competitor_win_rate : row.top_competitor_win_rate) || 0;
            return acc;
        }, {})
    ), [graphData, isAbsMode]);

    const presenceMetricKey = useMemo(() => {
        if (selected === 'blended') return 'blended';
        if (selected === 'paid') return 'paid';
        return 'winrate';
    }, [selected]);

    const domainPresenceScores = useMemo(() => {
        const scores = new Map<string, number>();
        aggregated.forEach((item: any) => {
            scores.set(item.domain_name, item[presenceMetricKey] ?? 0);
        });
        return scores;
    }, [aggregated, presenceMetricKey]);

    const clientDomainNames = useMemo(() => {
        const names = new Set<string>();
        if (workspace?.url) names.add(workspace.url);
        if (Array.isArray(workspace?.friendlyDomains)) {
            workspace.friendlyDomains.forEach((d: string) => { if (d) names.add(d); });
        }
        return names;
    }, [workspace?.url, workspace?.friendlyDomains]);

    const topCompetitors = useMemo(() =>
        [...aggregated].filter((d: any) => !clientDomainNames.has(d.domain_name)).sort((a: any, b: any) =>
            selected === 'blended'
                ? b.blended - a.blended
                : selected === 'paid'
                    ? b.paid - a.paid
                    : b.winrate - a.winrate
        ),
        [aggregated, clientDomainNames, selected]);

    const clientDomains = useMemo(() => {
        const metricKey = selected === 'paid' ? 'top_paid_presence_rate' : selected === 'blended' ? 'top_blended_presence_rate' : 'top_competitor_win_rate';
        return Array.from(clientDomainNames)
            .map(name =>
                allDomains.find((d: any) => d.domain_name === name) ??
                { domain_name: name, top_paid_presence_rate: 0, abs_top_paid_presence_rate: 0, top_blended_presence_rate: 0, abs_top_blended_presence_rate: 0, top_competitor_win_rate: 0, abs_top_competitor_win_rate: 0 }
            )
            .sort((a, b) => (b[metricKey] ?? 0) - (a[metricKey] ?? 0));
    }, [clientDomainNames, allDomains, selected]);

    // Phase 1 : domaines dérivés de graphData (chart déjà chargé) en attendant allDomains
    const graphDataDomains = useMemo(() => {
        if (allDomains.length > 0) return [];
        const byDomain = new Map<string, any>();
        for (const row of graphData) {
            const domain = row.domain_name;
            if (!domain || clientDomainNames.has(domain)) continue;
            const rowDate = row.date_group || row.date_day || '';
            const existing = byDomain.get(domain);
            if (!existing || rowDate > existing._date) {
                byDomain.set(domain, {
                    domain_name: domain,
                    top_blended_presence_rate: row.top_blended_presence_rate ?? 0,
                    abs_top_blended_presence_rate: row.abs_top_blended_presence_rate ?? 0,
                    top_paid_presence_rate: row.top_paid_presence_rate ?? 0,
                    abs_top_paid_presence_rate: row.abs_top_paid_presence_rate ?? 0,
                    top_competitor_win_rate: row.top_competitor_win_rate ?? 0,
                    abs_top_competitor_win_rate: row.abs_top_competitor_win_rate ?? 0,
                    _date: rowDate,
                });
            }
        }
        return Array.from(byDomain.values());
    }, [graphData, allDomains, clientDomainNames]);

    const sortedDomains = useMemo(() =>
        (allDomains.length > 0 ? allDomains : graphDataDomains)
            .filter((d: any) => {
                if (!d?.domain_name || clientDomainNames.has(d.domain_name)) return false;
                // Filter out domains with no presence rate to avoid clutter
                const presenceScore = domainPresenceScores.get(d.domain_name) ?? 0;
                return presenceScore > 0;
            })
            .sort(
                (a: any, b: any) =>
                    (domainPresenceScores.get(b.domain_name) ?? 0) -
                    (domainPresenceScores.get(a.domain_name) ?? 0)
            ),
        [allDomains, graphDataDomains, clientDomainNames, domainPresenceScores]);

    useEffect(() => {
        setGraphKeywords(filterStore?.keywords ?? []);
    }, [filterStore?.keywords]);

    useEffect(() => {
        return () => {
            dispatch(setFilterKeyword([]));
        };
    }, [dispatch]);

    const Row = ({ index, style, data }: any) => {
        const {
            competitors,
            theme,
            selectedDomains,
            topCompetitors,
            setSelectedDomains,
            selected,
            isAbsMode: isAbsModeRow,
            getDomainColor,
            selectedCountry: rowSelectedCountry,
        } = data;

        const [rowHovered, setRowHovered] = useState(false);
        const d = competitors[index];
        const isTopCompetitor = !hasUserSelected && topCompetitors.slice(0, 4).some((t: any) => t.domain_name === d.domain_name);
        const isSelected = selectedDomains.includes(d.domain_name);

        const rateLabel = selected === 'blended'
            ? formatRate(isAbsModeRow ? d.abs_top_blended_presence_rate : d.top_blended_presence_rate)
            : selected === 'paid'
                ? formatRate(isAbsModeRow ? d.abs_top_paid_presence_rate : d.top_paid_presence_rate)
                : formatRate(isAbsModeRow ? d.abs_top_competitor_win_rate : d.top_competitor_win_rate);

        const handleClick = () => {
            const effectivePrev = !hasUserSelected
                ? topCompetitors.slice(0, 4).map((t: any) => t.domain_name)
                : selectedDomains;

            if (effectivePrev.includes(d.domain_name)) {
                setHasUserSelected(true);
                setSelectedDomains(effectivePrev.filter((dom: string) => dom !== d.domain_name));
                return;
            }

            if (effectivePrev.length >= 4) {
                enqueueSnackbar(`You can select up to 4 domains only.`, { variant: 'warning' });
                return;
            }

            setHasUserSelected(true);
            setSelectedDomains([...effectivePrev, d.domain_name]);
        };

        const handleAdsShortcut = (e: React.MouseEvent) => {
            e.stopPropagation();
            dispatch(setFilterKeyword([]));
            const countryEntry = countriesJson?.find((c: any) => c.name === rowSelectedCountry);
            const code = countryEntry?.code?.toUpperCase() || '';
            navigate(`${dashboardPaths.visibilityAds}?country=${encodeURIComponent(code)}&domain=${encodeURIComponent(d.domain_name)}`);
        };

        return (
            <div style={style} key={d.domain_name}>
                <Box
                    onClick={handleClick}
                    onMouseEnter={() => setRowHovered(true)}
                    onMouseLeave={() => setRowHovered(false)}
                    sx={{
                        mb: 1.5,
                        cursor: 'pointer',
                        borderRadius: '6px',
                        '&:hover': {
                            bgcolor:
                                theme.palette.mode === 'dark'
                                    ? 'rgba(255,255,255,0.06)'
                                    : 'rgba(0,0,0,0.04)',
                        },
                        px: 2,
                        py: 0.25,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: .5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                <Box
                                    sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: isTopCompetitor || isSelected ? 1 : 0.4,
                                        bgcolor: theme.palette.mode === 'dark' ? '#0E131C' : '#E7EAEE',
                                        border: '2px solid',
                                        borderColor: getDomainColor(d.domain_name),
                                        padding: '2px',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            borderRadius: '50%',
                                            bgcolor: theme.palette.common.white,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <img
                                            src={`https://www.google.com/s2/favicons?domain=${d.domain_name}&sz=32`}
                                            alt={d.domain_name}
                                            style={{ width: 14, height: 14, borderRadius: '50%' }}
                                        />
                                    </Box>
                                </Box>

                                {(isTopCompetitor || isSelected) && (
                                    <Iconify
                                        icon="material-symbols:check-circle"
                                        height="13px"
                                        width="13px"
                                        sx={{
                                            position: 'absolute',
                                            right: -5,
                                            top: -3,
                                            color: isSelected ? '#00c853' : '#00c853',
                                        }}
                                    />
                                )}
                            </Box>
                            <Typography
                                variant="body2"
                                sx={{ opacity: isTopCompetitor || isSelected ? 1 : 0.4 }}
                            >
                                {d.domain_name}
                            </Typography>
                        </Box>

                        {rowHovered ? (
                            <Typography
                                variant="body2"
                                onClick={handleAdsShortcut}
                                sx={{
                                    fontWeight: 500,
                                    flexShrink: 0,
                                    color: '#006C9C',
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                See ads
                            </Typography>
                        ) : (
                            <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>
                                {rateLabel}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </div>
        );
    };

    // État de chargement pour les cartes : ne montrer les skeletons
    // que lors du premier chargement pour un workspace donné.
    const [hasLoadedCardsOnce, setHasLoadedCardsOnce] = useState(false);

    useEffect(() => {
        setHasLoadedCardsOnce(false);
    }, [workspace?.id]);

    useEffect(() => {
        if (!workspace?.id) return;
        if (hasLoadedCardsOnce) return;
        const anyLoading =
            isFetchingCompetitionEvolutionKpis ||
            isFetchingOverviewKpis ||
            isFetchingVisibilityCard ||
            isLoading ||
            competitionEvolutionKpis === undefined;
        if (!anyLoading) {
            setHasLoadedCardsOnce(true);
        }
    }, [
        workspace?.id,
        hasLoadedCardsOnce,
        isFetchingCompetitionEvolutionKpis,
        isFetchingOverviewKpis,
        isFetchingVisibilityCard,
        isLoading,
        competitionEvolutionKpis,
    ]);

    const cardBarLoading =
        !hasLoadedCardsOnce &&
        (
            isFetchingCompetitionEvolutionKpis ||
            isFetchingOverviewKpis ||
            isFetchingVisibilityCard ||
            isLoading ||
            competitionEvolutionKpis === undefined
        );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <CountryFilter
                loading={isLoading}
                countries={countriesData}
                selectedCountry={selectedCountry}
                onCountryChange={(c) => {
                    setSelectedCountry(c);
                    context?.setSelectedCountry(c);
                }}
                backgroundColor="transparent"
            />

            <Box px={'24px'} mt={isLoading ? '24px' : (countriesData && countriesData?.length > 1) ? '24px' : '0px'}>
                <CardBar
                    blendedPresenceTitle="Visibility Rate"
                    blendedPresenceTopMetric={blendedPresenceTopMetric}
                    blendedPresenceWeeklySeries={blendedPresenceRateWeeklySeries}
                    soloBidderTitle="Solo bidder rate"
                    soloBidderValue={soloBidderValue}
                    soloBidderWeeklySeries={soloBidderWeeklySeries}
                    topCompetitorsTitle="Top Competitors"
                    topCompetitorsData={topCompetitorsData}
                    dateRange="12 weeks"
                    loading={cardBarLoading}
                    selectedCountry={selectedCountry}
                    useNonBrandLayout
                />

                <Paper
                    elevation={0}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        borderRadius: 1,
                        border: '1px solid rgba(255,255,255,0.11)',
                        boxShadow: '0 7px 20px 0 rgba(255,255,255,0.04)',
                        background: 'transparent',
                        overflow: 'hidden',
                        mt: 2,
                    }}
                >
                    {/* BODY */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5, minHeight: 0, border: "1px solid rgba(145, 158, 171, 0.20)", borderRadius: "12px" }}>
                        <Box display="flex" sx={{ justifyContent: 'space-between', px: '32px', py: '10px', backgroundColor: '#F7F7F7', borderRadius: "12px" }}>
                            {/* Filter Buttons */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', border: '1px solid rgba(145,158,171,0.20)', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#fff', height: '36px', px: '6px' }}>
                                    {buttons.map((btn: any) => (
                                        <Tooltip
                                            key={btn.key}
                                            title={btn.tooltip}
                                            arrow
                                            placement="top"
                                            slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        backgroundColor: '#FFF',
                                                        color: '#000',
                                                        boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
                                                    }
                                                },
                                                arrow: {
                                                    sx: {
                                                        color: '#FFF',
                                                    }
                                                }
                                            }}
                                        >
                                            <Button
                                                disableElevation
                                                onClick={() => setSelected(btn.key)}
                                                disableRipple
                                                sx={(t) => ({
                                                    px: '10px',
                                                    py: 0,
                                                    minHeight: '24px',
                                                    height: '24px',
                                                    textTransform: 'none',
                                                    borderRadius: '6px',
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    lineHeight: '24px',
                                                    color: selected === btn.key ? (theme.palette.mode === 'dark' ? t.palette.primary.contrastText : 'black') : t.palette.text.secondary,
                                                    bgcolor: selected === btn.key
                                                        ? 'rgba(145, 158, 171, 0.2)'
                                                        : 'transparent',
                                                    '&:hover': {
                                                        bgcolor: selected === btn.key
                                                            ? theme.palette.mode === 'dark'
                                                                ? '#45515c'
                                                                : 'rgba(145,158,171,0.2)'
                                                            : theme.palette.mode === 'dark'
                                                                ? 'rgba(255,255,255,0.08)'
                                                                : 'rgba(0,0,0,0.04)',
                                                    },
                                                })}
                                                startIcon={<Iconify icon={btn.icon} sx={{ fontSize: 16 }} />}
                                            >
                                                <Typography sx={{ color: theme.palette.mode === 'dark' ? '#FFF' : 'black', fontSize: '13px', lineHeight: '20px' }}>
                                                    {btn.label}
                                                </Typography>
                                            </Button>
                                        </Tooltip>
                                    ))}
                                </Box>
                            </Box>

                            {/* Abs Top Selector */}
                            <Box display="flex" flexDirection="row">
                                <Box sx={{ display: 'flex', height: 36, p: '6px', alignItems: 'center', gap: '2px', borderRadius: '8px' }}>
                                    <Box sx={{ flexShrink: 0 }}>
                                        <InfiniteScrollKeywords
                                            keywords={(filterStore?.loadedKeywords || [])}
                                            onKeywordChange={(it: any) => handleKeywordFilterChange(it)}
                                        />
                                    </Box>
                                    <SearchTermAnalysisHeader isAbsMode={isAbsMode} onToggleAbsMode={() => setIsAbsMode((v: any) => !v)} />
                                </Box>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                    <Select
                                        value={isAbsMode ? 'abs' : 'top'}
                                        onChange={(e) => setIsAbsMode(e.target.value === 'abs')}
                                        displayEmpty
                                        renderValue={(selected) => (
                                            <Chip
                                                label={selected === 'abs' ? 'Absolute Top' : 'Top'}
                                                sx={{
                                                    bgcolor: 'rgba(145,158,171,0.20)',
                                                    color: theme.palette.mode === 'dark' ? '#fff' : 'black',
                                                    fontSize: '13px',
                                                    height: 24,
                                                    borderRadius: '6px',
                                                    '& .MuiChip-label': {
                                                        px: 1.5,
                                                        lineHeight: '20px',
                                                    },
                                                }}
                                            />
                                        )}
                                        sx={{
                                            borderRadius: '8px',
                                            '& .MuiSelect-select': {
                                                padding: '0 8px',
                                            },
                                            height: 36,
                                            fontSize: '14px',
                                            '.MuiOutlinedInput-notchedOutline': {
                                                border: '1px solid rgba(145,158,171,0.20)',
                                            },
                                            '.MuiSelect-icon': {
                                                color: theme.palette.mode === 'dark' ? '#fff' : 'black',
                                            },
                                        }}
                                    >
                                        <MenuItem value="top">Top</MenuItem>
                                        <MenuItem value="abs">Absolute Top</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        {/* Chart + Sidebar */}

                        <Box sx={{ display: 'flex', width: '100%', height: 600, borderTop: '1px solid #E9ECEE', backgroundColor: "white" }}>
                            {/* Chart */}
                            {(!selectedCountry || isFetching || !data || (allDomainNames.length > 0 && !brandColorsReady)) ? (
                                <Skeleton width="100%" height="calc(100vh - 280px)" variant="rectangular" animation="wave" />
                            ) : (
                                <ChartEvolution
                                    data={data?.data?.presence_rates_average_metrics_by_date}
                                    rateMode={selected}
                                    isAbsMode={isAbsMode}
                                    mainDomain={workspace?.url}
                                    activeDomains={[
                                        ...(workspace?.url ? [workspace.url] : []),
                                        ...(Array.isArray(workspace?.friendlyDomains) ? workspace.friendlyDomains.filter(Boolean) : []),
                                        ...(hasUserSelected ? selectedDomains : topCompetitors.slice(0, 4).map((t: any) => t.domain_name)),
                                    ]}
                                    getColor={getDomainColor}
                                />
                            )}

                            {/* Sidebar domaines */}
                            {(isLoadingAvailableDomains || (clientDomains.length === 0 && sortedDomains.length === 0))
                                ? <Skeleton width={400} height="calc(100vh - 280px)" variant="rectangular" animation="wave" />
                                :

                                <Box
                                    sx={{
                                        width: 400,
                                        borderLeft: theme.palette.mode === 'dark' ? '1px solid #40484f' : '1px solid #E9ECEE',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        backgroundColor: '#f9f9fa'
                                    }}
                                >
                                    <Box px={2} display="flex" justifyContent="flex-end" textAlign="left">
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#B1B9C4',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                mb: 0.5,
                                                mt: 1.5
                                            }}
                                        >
                                            {selected === "blended" && "Visibility Rate" || selected === "paid" && "Paid PR"}
                                        </Typography>
                                    </Box>

                                    {/* Bloc fixe : domaines client (url + friendly) + search input */}
                                    {clientDomains.map((d: any) => {
                                        return (
                                            <Box key={d.domain_name} sx={{ flexShrink: 0 }}>

                                                {/* Client domain + valeur */}
                                                <Box
                                                    onMouseEnter={() => setHoveredClientDomain(d.domain_name)}
                                                    onMouseLeave={() => setHoveredClientDomain(null)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        px: 2,
                                                        py: 0.5,
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                                        <Box
                                                            sx={{
                                                                width: 26,
                                                                height: 26,
                                                                borderRadius: '50%',
                                                                overflow: 'hidden',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                bgcolor: theme.palette.mode === 'dark' ? '#0E131C' : '#E7EAEE',
                                                                border: '2px solid',
                                                                borderColor: getDomainColor(d.domain_name),
                                                                padding: '2px',
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    borderRadius: '50%',
                                                                    bgcolor: theme.palette.common.white,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <img
                                                                    src={`https://www.google.com/s2/favicons?domain=${d.domain_name}&sz=32`}
                                                                    alt={d.domain_name}
                                                                    style={{ width: 14, height: 14, borderRadius: '50%' }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {d.domain_name}
                                                        </Typography>
                                                    </Box>

                                                    {hoveredClientDomain === d.domain_name ? (
                                                        <Typography
                                                            variant="body2"
                                                            onClick={() => {
                                                                dispatch(setFilterKeyword([]));
                                                                const countryEntry = countriesJson?.find((c: any) => c.name === selectedCountry);
                                                                const code = countryEntry?.code?.toUpperCase() || '';
                                                                navigate(`${dashboardPaths.visibilityAds}?country=${encodeURIComponent(code)}&domain=${encodeURIComponent(d.domain_name)}`);
                                                            }}
                                                            sx={{
                                                                fontWeight: 500,
                                                                color: '#006C9C',
                                                                cursor: 'pointer',
                                                                '&:hover': { textDecoration: 'underline' },
                                                            }}
                                                        >
                                                            See ads
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {selected === 'blended'
                                                                ? formatRate(isAbsMode ? d.abs_top_blended_presence_rate : d.top_blended_presence_rate)
                                                                : selected === 'paid'
                                                                    ? formatRate(isAbsMode ? d.abs_top_paid_presence_rate : d.top_paid_presence_rate)
                                                                    : formatRate(isAbsMode ? d.abs_top_competitor_win_rate : d.top_competitor_win_rate)}
                                                        </Typography>
                                                    )}
                                                </Box>

                                            </Box>
                                        );
                                    })}

                                    {/* Search input */}
                                    <Box sx={{ my: 1, borderBottom: theme.palette.mode === 'dark' ? '1px solid #40484f' : '1px solid #E9ECEE' }} />
                                    <Box sx={{ px: 1.5, py: 1.5 }}>
                                        <TextField
                                            fullWidth
                                            sx={{ backgroundColor: "rgba(145, 158, 171, 0.12)" }}
                                            size="small"
                                            placeholder="Search competitors..."
                                            value={domainFilter}
                                            onChange={(e) => setDomainFilter(e.target.value)}
                                        />
                                    </Box>

                                    {/* Bloc scrollable : compétiteurs */}
                                    <Box
                                        sx={{
                                            flex: 1,
                                            overflow: 'hidden',
                                            pr: 0.5,
                                        }}
                                    >
                                        {(() => {
                                            const competitors = sortedDomains.filter(
                                                (d) =>
                                                    d?.domain_name?.toLowerCase().includes(domainFilter.toLowerCase())
                                            );

                                            if (competitors.length === 0) {
                                                return (
                                                    <Box
                                                        key="no-results"
                                                        sx={{
                                                            px: 2,
                                                            py: 2,
                                                            textAlign: 'center',
                                                            color: theme.palette.text.secondary,
                                                            fontStyle: 'italic',
                                                        }}
                                                    >
                                                        No results
                                                    </Box>
                                                );
                                            }

                                            return (
                                                <List
                                                    height={440}
                                                    itemCount={competitors.length}
                                                    itemSize={ITEM_HEIGHT}
                                                    width="100%"
                                                    itemData={{
                                                        competitors,
                                                        theme,
                                                        selectedDomains,
                                                        topCompetitors,
                                                        setSelectedDomains,
                                                        selected,
                                                        isAbsMode,
                                                        getDomainColor,
                                                        selectedCountry,
                                                    }}
                                                >
                                                    {Row}
                                                </List>
                                            );
                                        })()}
                                    </Box>

                                </Box>
                            }

                        </Box>
                    </Box>
                </Paper >
            </Box>
        </Box >
    );
}
